import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  X, UserMinus, MapPin, Clock, ArrowRightLeft, Plus, Trash2,
  Lock, AlertTriangle, CheckCircle2, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getBackendUrl } from '../utils/config';

const API_URL = getBackendUrl();

const TIPOS_ALTERACAO = {
  ausencia: { label: 'Ausencia', icon: UserMinus, color: 'text-red-600 bg-red-50 border-red-200' },
  mudanca_local: { label: 'Mudar Local', icon: MapPin, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  mudanca_turno: { label: 'Mudar Turno', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  substituicao: { label: 'Substituir', icon: ArrowRightLeft, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  turno_extra: { label: 'Turno Extra', icon: Plus, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
};

const LOCAIS = ['Cafe', 'Loja Satao', 'Loja Avelal'];

const DiaOverridesPanel = ({ data, isOpen, onClose, colaboradoresHorarios, colaboradoresAtivos, onOverrideSaved }) => {
  const [alteracoes, setAlteracoes] = useState([]);
  const [fechoDia, setFechoDia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formTipo, setFormTipo] = useState('');
  const [selectedColab, setSelectedColab] = useState('');
  const [formData, setFormData] = useState({
    novo_local: '', nova_entrada: '', nova_saida: '',
    substituto_id: '', motivo: '',
  });
  const [fechoLoading, setFechoLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    if (!isOpen || !data) return;
    setLoading(true);
    try {
      const [altRes, fechoRes] = await Promise.all([
        axios.get(`${API_URL}/api/alteracoes-dia`, { params: { data }, headers }),
        axios.get(`${API_URL}/api/fechos-dia/${data}`, { headers }),
      ]);
      setAlteracoes(altRes.data || []);
      setFechoDia(fechoRes.data);
    } catch (err) {
      console.error('Erro ao carregar dados do dia:', err);
    } finally {
      setLoading(false);
    }
  }, [isOpen, data]);

  useEffect(() => { loadData(); }, [loadData]);

  // Escala real do dia: extrair turnos de colaboradoresHorarios para esta data
  const turnosDoDia = [];
  if (colaboradoresHorarios && data) {
    for (const colab of colaboradoresHorarios) {
      const registos = colab.registos_por_dia?.[data] || [];
      for (const r of registos) {
        if (r.tipo === 'normal' || r.tipo === 'apoio') {
          turnosDoDia.push({
            colaborador_id: colab.colaborador_id,
            colaborador_nome: colab.colaborador_nome,
            local: r.local,
            entrada: r.entrada,
            saida: r.saida,
            tipo: r.tipo,
          });
        }
      }
    }
  }

  const handleSubmit = async () => {
    if (!selectedColab || !formTipo) return;
    const colab = turnosDoDia.find(t => t.colaborador_id === selectedColab)
      || colaboradoresAtivos?.find(c => c.id === selectedColab);
    if (!colab) return;

    const colabNome = colab.colaborador_nome || colab.nome || '';
    const colabTurno = turnosDoDia.find(t => t.colaborador_id === selectedColab);

    const payload = {
      data,
      tipo_alteracao: formTipo,
      colaborador_id: selectedColab,
      colaborador_nome: colabNome,
      original_local: colabTurno?.local || '',
      original_entrada: colabTurno?.entrada || '',
      original_saida: colabTurno?.saida || '',
      novo_local: formData.novo_local || null,
      nova_entrada: formData.nova_entrada || null,
      nova_saida: formData.nova_saida || null,
      substituto_id: formData.substituto_id || null,
      substituto_nome: formData.substituto_id
        ? (colaboradoresAtivos?.find(c => c.id === formData.substituto_id)?.nome || '')
        : null,
      motivo: formData.motivo || '',
    };

    try {
      await axios.post(`${API_URL}/api/alteracoes-dia`, payload, { headers });
      toast.success(`Override criado: ${TIPOS_ALTERACAO[formTipo]?.label}`);
      setShowForm(false);
      setFormTipo('');
      setSelectedColab('');
      setFormData({ novo_local: '', nova_entrada: '', nova_saida: '', substituto_id: '', motivo: '' });
      await loadData();
      if (onOverrideSaved) onOverrideSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar override');
    }
  };

  const handleRemover = async (id) => {
    if (!window.confirm('Remover esta alteracao?')) return;
    try {
      await axios.delete(`${API_URL}/api/alteracoes-dia/${id}`, { headers });
      toast.success('Override removido');
      await loadData();
      if (onOverrideSaved) onOverrideSaved();
    } catch (err) {
      toast.error('Erro ao remover');
    }
  };

  const handleFecharDia = async () => {
    if (!window.confirm(`FECHAR DIA ${data}?\n\nIsto gera um snapshot definitivo.\nSo admin pode reabrir.`)) return;
    setFechoLoading(true);
    try {
      await axios.post(`${API_URL}/api/fechos-dia/${data}`, null, { headers });
      toast.success(`Dia ${data} fechado com sucesso`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fechar dia');
    } finally {
      setFechoLoading(false);
    }
  };

  const handleReabrirDia = async () => {
    if (!window.confirm(`Reabrir dia ${data}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/fechos-dia/${data}`, { headers });
      toast.success(`Dia ${data} reaberto`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reabrir');
    }
  };

  if (!isOpen) return null;

  const dateObj = new Date(data + 'T00:00:00');
  const diasSemana = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
  const diaFormatado = `${diasSemana[dateObj.getDay()]}, ${dateObj.getDate()} ${dateObj.toLocaleDateString('pt-PT', { month: 'long' })} ${dateObj.getFullYear()}`;
  const isDiaFechado = fechoDia?.status === 'fechado';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        data-testid="dia-panel-overlay"
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-background border-l shadow-2xl z-50 flex flex-col overflow-hidden"
        data-testid="dia-overrides-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-muted/30">
          <div>
            <h2 className="text-base font-bold" data-testid="dia-panel-titulo">{diaFormatado}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Excepcoes operacionais — o horario base nao e alterado
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="dia-panel-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content — scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Estado do dia */}
          {isDiaFechado && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="dia-fechado-banner">
              <Lock className="h-4 w-4 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">Dia fechado</p>
                <p className="text-xs text-amber-600">
                  Por {fechoDia.fechado_por_nome} em {new Date(fechoDia.fechado_em).toLocaleString('pt-PT')}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={handleReabrirDia} className="text-xs h-7" data-testid="dia-reabrir-btn">
                Reabrir
              </Button>
            </div>
          )}

          {/* Escala real do dia */}
          <div data-testid="dia-escala-real">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Escala do dia</span>
              <Badge variant="outline" className="text-[10px]">{turnosDoDia.length}</Badge>
            </div>
            {turnosDoDia.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2">Sem turnos neste dia</p>
            ) : (
              <div className="space-y-1">
                {turnosDoDia.map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-muted/30 rounded text-sm">
                    <span className="font-medium">{t.colaborador_nome}</span>
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="text-[10px]">{t.local}</Badge>
                      <span className="text-muted-foreground">{t.entrada}-{t.saida}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Overrides registados */}
          <div data-testid="dia-overrides-lista">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Alteracoes registadas</span>
              <Badge variant="outline" className="text-[10px]">{alteracoes.length}</Badge>
            </div>
            {loading ? (
              <p className="text-xs text-muted-foreground py-2">A carregar...</p>
            ) : alteracoes.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2">Nenhuma excepcao registada</p>
            ) : (
              <div className="space-y-1.5">
                {alteracoes.map((alt) => {
                  const tipoInfo = TIPOS_ALTERACAO[alt.tipo_alteracao] || {};
                  const Icon = tipoInfo.icon || AlertTriangle;
                  return (
                    <div key={alt.id} className={`flex items-center gap-2 py-2 px-3 rounded-lg border ${tipoInfo.color || 'bg-muted/30'}`}>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold">{alt.colaborador_nome}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {alt.tipo_alteracao === 'ausencia' && 'Ausente'}
                          {alt.tipo_alteracao === 'mudanca_local' && `${alt.original_local || '?'} → ${alt.novo_local}`}
                          {alt.tipo_alteracao === 'mudanca_turno' && `${alt.nova_entrada || '?'}-${alt.nova_saida || '?'}`}
                          {alt.tipo_alteracao === 'substituicao' && `Subst. por ${alt.substituto_nome}`}
                          {alt.tipo_alteracao === 'turno_extra' && `Extra: ${alt.novo_local} ${alt.nova_entrada}-${alt.nova_saida}`}
                          {alt.motivo && ` | ${alt.motivo}`}
                        </p>
                      </div>
                      {!isDiaFechado && (
                        <Button variant="ghost" size="sm" onClick={() => handleRemover(alt.id)} className="h-6 w-6 p-0 text-red-400 hover:text-red-600">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Acoes — criar override */}
          {!isDiaFechado && (
            <div data-testid="dia-acoes">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-2">Registar excepcao</span>

              {!showForm ? (
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(TIPOS_ALTERACAO).map(([tipo, info]) => {
                    const Icon = info.icon;
                    return (
                      <Button
                        key={tipo}
                        variant="outline"
                        size="sm"
                        className="justify-start text-xs h-9"
                        onClick={() => { setFormTipo(tipo); setShowForm(true); }}
                        data-testid={`dia-acao-${tipo}`}
                      >
                        <Icon className="h-3.5 w-3.5 mr-1.5" />
                        {info.label}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <Card className="border-dashed" data-testid="dia-override-form">
                  <CardContent className="p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge className={TIPOS_ALTERACAO[formTipo]?.color}>
                        {TIPOS_ALTERACAO[formTipo]?.label}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setFormTipo(''); }} className="h-6 w-6 p-0">
                        <X className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Colaborador */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Colaborador</label>
                      <select
                        value={selectedColab}
                        onChange={(e) => setSelectedColab(e.target.value)}
                        className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                        data-testid="dia-form-colab"
                      >
                        <option value="">Selecionar...</option>
                        {turnosDoDia.map((t, i) => (
                          <option key={`turno-${i}`} value={t.colaborador_id}>
                            {t.colaborador_nome} ({t.local} {t.entrada}-{t.saida})
                          </option>
                        ))}
                        {formTipo === 'turno_extra' && colaboradoresAtivos?.filter(c => !turnosDoDia.some(t => t.colaborador_id === c.id)).map(c => (
                          <option key={`extra-${c.id}`} value={c.id}>{c.nome} (sem turno)</option>
                        ))}
                      </select>
                    </div>

                    {/* Campos condicionais */}
                    {(formTipo === 'mudanca_local' || formTipo === 'turno_extra') && (
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Novo local</label>
                        <select
                          value={formData.novo_local}
                          onChange={(e) => setFormData(p => ({ ...p, novo_local: e.target.value }))}
                          className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                          data-testid="dia-form-local"
                        >
                          <option value="">Selecionar...</option>
                          {LOCAIS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                    )}

                    {(formTipo === 'mudanca_turno' || formTipo === 'turno_extra') && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Entrada</label>
                          <input
                            type="time" value={formData.nova_entrada}
                            onChange={(e) => setFormData(p => ({ ...p, nova_entrada: e.target.value }))}
                            className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                            data-testid="dia-form-entrada"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Saida</label>
                          <input
                            type="time" value={formData.nova_saida}
                            onChange={(e) => setFormData(p => ({ ...p, nova_saida: e.target.value }))}
                            className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                            data-testid="dia-form-saida"
                          />
                        </div>
                      </div>
                    )}

                    {formTipo === 'substituicao' && (
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Substituto</label>
                        <select
                          value={formData.substituto_id}
                          onChange={(e) => setFormData(p => ({ ...p, substituto_id: e.target.value }))}
                          className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                          data-testid="dia-form-substituto"
                        >
                          <option value="">Selecionar...</option>
                          {colaboradoresAtivos?.filter(c => c.id !== selectedColab).map(c => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Motivo */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Motivo (opcional)</label>
                      <input
                        type="text" value={formData.motivo} placeholder="Ex: doenca, troca voluntaria..."
                        onChange={(e) => setFormData(p => ({ ...p, motivo: e.target.value }))}
                        className="w-full border rounded px-2 py-1.5 text-xs bg-background"
                        data-testid="dia-form-motivo"
                      />
                    </div>

                    <Button size="sm" className="w-full" onClick={handleSubmit} disabled={!selectedColab} data-testid="dia-form-guardar">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      Guardar
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Footer — Fechar Dia */}
        <div className="p-4 border-t bg-muted/10">
          {isDiaFechado ? (
            <div className="flex items-center gap-2 text-xs text-amber-600">
              <Lock className="h-3.5 w-3.5" />
              <span>Dia fechado — snapshot definitivo gerado</span>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={handleFecharDia}
              disabled={fechoLoading}
              data-testid="dia-fechar-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              {fechoLoading ? 'A fechar...' : 'Fechar Dia'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default DiaOverridesPanel;
